import { z } from "zod";
import type { ConfigInput } from "../types/config";

const sideSchema = z.enum(["top", "right", "bottom", "left"]);

const processingRequestSchema = z.object({
  kind: z.enum(["boring", "uitsparing", "hoek"]),
  sizeMm: z.number().positive(),
  count: z.number().int().min(1),
  finished: z.boolean().optional(),
  hoekType: z.enum(["rond", "schuin-poli", "schuin-brut", "gebroken"]).optional(),
});

const hingePrepSchema = z.object({
  slug: z.string().max(200).optional(),
  naam: z.string().trim().min(1).max(200),
  meebesteld: z.boolean(),
  zijde: z.enum(["links", "rechts"]),
  hartenMm: z.array(z.number().nonnegative().max(20000)).min(1).max(4),
});

const knobPrepSchema = z.object({
  slug: z.string().max(200).optional(),
  naam: z.string().trim().min(1).max(200),
  meebesteld: z.boolean(),
  hartMm: z.number().nonnegative().max(20000),
  randMm: z.number().nonnegative().max(5000),
  boringMm: z.number().positive().max(200).optional(),
  gaten: z.number().int().min(1).max(2).optional(),
  hohMm: z.number().positive().max(2000).optional(),
  uitsparingMm: z.number().positive().max(500).optional(),
});

/** Runtime validation of client input before pricing (used by the server). */
export const configInputSchema = z.object({
  variantId: z.string().min(1),
  measureType: z.enum(["sponning", "glas"]),
  widthMm: z.number().positive(),
  heightMm: z.number().positive(),
  quantity: z.number().int().min(1),
  shapeId: z.string().min(1).optional(),
  edgeFinishId: z.string().min(1).optional(),
  selectedSides: z.array(sideSchema).optional(),
  processings: z.array(processingRequestSchema).optional(),
  ral: z.string().trim().max(12).optional(),
  ralBinnen: z.string().trim().max(12).optional(),
  keurmerkZichtbaar: z.boolean().optional(),
  extraFolie: z.union([z.literal("blank"), z.literal("mat")]).optional(),
  hinges: hingePrepSchema.optional(),
  knob: knobPrepSchema.optional(),
  facetZijde: z.union([z.literal("mat"), z.literal("glad")]).optional(),
  rotationDeg: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]).optional(),
});

// Compile-time drift guard: the parsed shape must be a valid ConfigInput.
const _assertConfigInput: (x: z.infer<typeof configInputSchema>) => ConfigInput = (
  x,
) => x;
void _assertConfigInput;
